import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authorizeClient, client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';
import moment from 'moment';

export interface IDocumentType {
  attachment_type_id: number;
  attachment_type_description: string;
}
export interface IDocument {
  id: string;
  attachment_id?: number;
  attachment_type_id: number;
  attachment_url: string;
  datetime_created: Date;
  description?: string;
  link: string;
  name: string;
  title: string;
  mime_type: string;
  transcription_text?: string;
  transcriptionLanguageId?: number;
  file_extension?: string;
  memo?: string;
  fileSize?: number;
  person_firstname?: string;
  person_lastname?: string;
  person_designation?: string;
  comment: string;
  attachment_type_name?: string;
  attachment_type_description?: string;
}

export interface IDocumentPerPerson {
  attachment_type_id: number;
  datetime_created: Date;
  file_extension: string;
  link: string;
  name: string;
  memo: string;
  title: string;
  description: string;
  attachment_type_description?: string;
}
export interface IDocumentInput {
  document: IDocument;
  file: File;
}

export interface DocumentsState {
  getStatus: string;
  addStatus: string;
  deleteStatus: string;
  base64Document: string;
  getDocumentBase64Status: string;
  documents: IDocument[];
  documentsPerPerson: IDocument[];
  documentTypes: IDocumentType[];
  documentTypesStatus: string;
  documentsToUpload: IDocumentInput[];
}

const initialState: DocumentsState = {
  getStatus: 'idle',
  getDocumentBase64Status: 'idle',
  addStatus: 'idle',
  deleteStatus: 'idle',
  documents: [],
  base64Document: '',
  documentsPerPerson: [],
  documentTypes: [],
  documentTypesStatus: 'idle',
  documentsToUpload: [],
};

export interface fetchDocuments {
  salesOrderId: number;
  salesOrderItemId: number;
}
export interface fetchDocumentsPerOrder {
  accountNumber: number;
  taId: number;
  orderId: number;
  orderItemId: number;
}

export const fetchDocumentTypes = createAsyncThunk(
  'documents/fetchDocumentTypes',
  async (_, { rejectWithValue }) => {
    try {
      const documentTypesResponse: any = await authorizeClient.get(
        '/core/public/health_attachment_types'
      );
      return documentTypesResponse.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const fetchDocuments = createAsyncThunk(
  'documents/fetchAll',
  async (fetchDocuments: fetchDocuments, { dispatch, rejectWithValue, getState }) => {
    try {
      const response: any = await client.get(
        `/healthpoint/attachments/${fetchDocuments.salesOrderId}/${fetchDocuments.salesOrderItemId}`
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const getCustomerAttachments = createAsyncThunk(
  'attachments/fetch',
  async ({ params, document_type, customer_person_id }: any, { rejectWithValue }) => {
    try {
      let fileName: string = 'result.pdf';
      if (document_type === 'a') {
        fileName = `anamnesis_${moment(params.date_to).format('YYYY-MM-DD')}`;
      } else if (document_type === 'v') {
        fileName = `${params.group_info.groupName}_${params.date_from}_${params.date_to}`;
      }

      const queryParams = { ...params };
      if (queryParams.group_info) {
        queryParams['data_group_id'] = params.group_info.groupId;
        delete queryParams.group_info;
      }

      const response = await client.get(
        `/core/merchant/${customer_person_id}/attachment/${document_type}`,
        {
          params: queryParams,
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const fetchDocumentsPerPerson = createAsyncThunk(
  'documents/fetchDocumentsPerPersonAll',
  async (fetchDocuments: any, { dispatch, rejectWithValue, getState }) => {
    try {
      const response: any = await authorizeClient.get(
        `/core/merchant/upload_document/${fetchDocuments.accountNumber}`
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const fetchBase64 = createAsyncThunk(
  'documents/fetchBase64',
  async (documentFilename: string, { dispatch, rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const response: any = await client.get(
        `/healthpoint/base64-document/${documentFilename}/${state?.auth?.businessPartnerId}`
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

interface UploadDocumentBody {
  salesOrderId: number;
  salesOrderItemId: number;
  attachmentTypeId: number;
  formData: any;
}

export const uploadDocument = createAsyncThunk(
  'documents/upload',
  async (uploadDocumentBody: UploadDocumentBody, { dispatch, rejectWithValue, getState }) => {
    try {
      const response: any = await client.post(
        `/healthpoint/attachments/${uploadDocumentBody.attachmentTypeId}/${uploadDocumentBody.salesOrderId}/${uploadDocumentBody.salesOrderItemId}`,
        uploadDocumentBody.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      dispatch(
        fetchDocuments({
          salesOrderId: uploadDocumentBody.salesOrderId,
          salesOrderItemId: uploadDocumentBody.salesOrderItemId,
        })
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const uploadDocumentsPerPerson = createAsyncThunk(
  'documents/upload',
  async (uploadDocumentBody: any, { dispatch, rejectWithValue, getState }) => {
    try {
      const response: any = await authorizeClient.post(
        `/core/merchant/upload_document/${uploadDocumentBody.accountNumber}`,
        uploadDocumentBody.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      dispatch(
        fetchDocumentsPerPerson({
          accountNumber: uploadDocumentBody.accountNumber,
        })
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const deleteDocument = createAsyncThunk(
  'document/delete',
  async (documentId: string, { dispatch, rejectWithValue }) => {
    try {
      const response: any = await client.delete(`/healthpoint/document/${documentId}`);
      // dispatch(fetchDocuments());
      return response;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    resetDocuments: (_) => {
      return initialState;
    },
    resetAddDocument: (state) => {
      return {
        ...state,
        addStatus: 'idle',
      };
    },
    resetDeleteDocument: (state) => {
      return {
        ...state,
        deleteStatus: 'idle',
      };
    },
    setDocumentsToUpload: (state, action) => {
      return {
        ...state,
        documentsToUpload: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentTypes.pending, (state) => {
        state.documentTypesStatus = 'pending';
      })
      .addCase(fetchDocumentTypes.fulfilled, (state, action) => {
        state.documentTypes = action.payload;
        state.documentTypesStatus = 'fulfilled';
      })
      .addCase(fetchDocumentTypes.rejected, (state, action: any) => {
        toast.error(
          action?.payload?.error ?? 'Something went wrong with fetching document types.',
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
        state.documentTypesStatus = 'rejected';
      })
      .addCase(fetchDocuments.pending, (state) => {
        state.getStatus = 'pending';
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.documents = action.payload.data.attachments;
        state.getStatus = 'fulfilled';
      })
      .addCase(fetchDocuments.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.documents = [];
        state.getStatus = 'rejected';
      })
      .addCase(fetchDocumentsPerPerson.pending, (state) => {
        state.getStatus = 'pending';
      })
      .addCase(fetchDocumentsPerPerson.fulfilled, (state, action) => {
        state.documentsPerPerson = action.payload.data;
        state.getStatus = 'fulfilled';
      })
      .addCase(fetchDocumentsPerPerson.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.documentsPerPerson = [];
        state.getStatus = 'rejected';
      })
      .addCase(getCustomerAttachments.rejected, (state, action: any) => {
        state.documentsPerPerson = [];
        state.getStatus = 'rejected';
      })
      .addCase(fetchBase64.pending, (state) => {
        state.getDocumentBase64Status = 'pending';
      })
      .addCase(fetchBase64.fulfilled, (state, action) => {
        state.base64Document = action.payload.data.document;
        state.getDocumentBase64Status = 'fulfilled';
      })
      .addCase(fetchBase64.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.getDocumentBase64Status = 'rejected';
      })
      .addCase(uploadDocument.pending, (state) => {
        state.addStatus = 'pending';
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        toast.success('Successfully uploaded the document', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.addStatus = 'fulfilled';
      })
      .addCase(uploadDocument.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.addStatus = 'rejected';
      })
      .addCase(deleteDocument.pending, (state) => {
        state.deleteStatus = 'pending';
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        toast.success('Successfully deleted the document', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.deleteStatus = 'fulfilled';
      })
      .addCase(deleteDocument.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.deleteStatus = 'rejected';
      });
  },
});

export const { resetDocuments, resetAddDocument, resetDeleteDocument, setDocumentsToUpload } =
  documentsSlice.actions;

export default documentsSlice.reducer;

export const selectDocumentTypes = (state: RootState) => state.documents.documentTypes;
export const selectDocuments = (state: RootState) => state.documents.documents;
export const selectDocumentsPerPerson = (state: RootState) => state.documents.documentsPerPerson;
export const selectBase64Document = (state: RootState) => state.documents.base64Document;
export const selectBase64DocumentStatus = (state: RootState) =>
  state.documents.getDocumentBase64Status;
export const selectDocumentAddStatus = (state: RootState) => state.documents.addStatus;
export const selectDocumentsGetStatus = (state: RootState) => state.documents.getStatus;
export const selectDocumentDeleteStatus = (state: RootState) => state.documents.deleteStatus;
export const selectDocumentTypesStatus = (state: RootState) => state.documents.documentTypesStatus;
export const selectDocumentToUpload = (state: RootState) => state.documents.documentsToUpload;
export const selectDocStatus = (state: RootState) => state.documents.getStatus;
